
document.addEventListener('DOMContentLoaded', () => {
  // Request the Customer Privacy API feature. This script is loaded with
  // `defer`, so it always runs after {{ content_for_header }} — window.Shopify
  // is guaranteed to exist here. A prior pass (2026-07-17) dropped this call
  // after live testing suggested the API loaded without it; BO review
  // (2026-07-20) confirmed that was wrong — window.Shopify.customerPrivacy
  // is undefined on the storefront without this call. Restored.
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      // shopifyCustomerPrivacyApiLoaded is not reliably dispatched after this
      // call: on a returning visitor's page load the stored choice was never
      // applied, so GA4 and GTM were not injected although consent was
      // recorded (11/10/2026 live read on 200267202908: dispatching the event
      // by hand applied it). Apply the stored choice here whether the call
      // succeeded or failed. applyStoredConsent() only acts on a stored
      // choice, loadAnalytics() is idempotent, and setConsent() carries its
      // own retry loop, so a second application via the event is harmless.
      // Deferred by one tick: applyStoredConsent is declared further down this
      // handler, so a callback invoked synchronously must not reach it early.
      setTimeout(applyStoredConsent, 0);
    });
  }

  // Helper to safely call the Privacy API
  let retryCount = 0;
  const MAX_RETRIES = 5;

  const setConsent = (status, callback) => {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      window.Shopify.customerPrivacy.setTrackingConsent(status, () => {
        if (callback) callback();
      });
    } else if (retryCount < MAX_RETRIES) {
      retryCount++;
      setTimeout(() => setConsent(status, callback), 1000);
    }
  };

  // Google Consent Mode v2: update GA4 / GTM consent state to match the
  // Termly choice. `gtag` is defined unconditionally in theme.liquid
  // (default consent is also set there, before GA/GTM load), so it's always
  // available here even before GA/GTM finish loading.
  const updateGtagConsent = (analytics, advertising) => {
    if (typeof window.gtag !== 'function') return;
    const a = analytics ? 'granted' : 'denied';
    const m = advertising ? 'granted' : 'denied';
    window.gtag('consent', 'update', {
      'analytics_storage': a,
      'ad_storage': m,
      'ad_user_data': m,
      'ad_personalization': m
    });
  };

  // JORS-3856: apply a per-category Termly choice. Termly categories map to
  // the Shopify Customer Privacy purposes as follows:
  //   analytics   -> analytics
  //   advertising -> marketing, sale_of_data
  //   performance -> preferences
  const applyCategories = (c) => {
    const analytics = !!c.analytics;
    const advertising = !!c.advertising;
    const performance = !!c.performance;
    updateGtagConsent(analytics, advertising);
    // Inject GA4 + GTM only when analytics is granted (idempotent loader
    // defined in theme.liquid). Never called on a refusal.
    if (analytics && typeof window.loadAnalytics === 'function') window.loadAnalytics();
    setConsent({ 'analytics': analytics, 'marketing': advertising, 'preferences': performance, 'sale_of_data': advertising });
  };

  const ALL_ON = { analytics: true, advertising: true, performance: true };
  const ALL_OFF = { analytics: false, advertising: false, performance: false };

  const enableTracking = () => applyCategories(ALL_ON);
  const disableTracking = () => applyCategories(ALL_OFF);

  // Persist a choice made anywhere Termly exposes it (banner or preference
  // centre). termly-consent-status keeps its two historical values for an
  // all-or-nothing choice and takes 'custom' for a mixed one; the categories
  // themselves are kept in termly-consent-categories.
  const storeCategories = (c) => {
    const picked = { analytics: !!c.analytics, advertising: !!c.advertising, performance: !!c.performance };
    const on = Object.values(picked).filter(Boolean).length;
    const status = on === 3 ? 'accepted' : (on === 0 ? 'declined' : 'custom');
    localStorage.setItem('termly-consent-status', status);
    localStorage.setItem('termly-consent-categories', JSON.stringify(picked));
    return picked;
  };

  // Apply the stored consent choice as soon as the Privacy API is available.
  // Check first, in case it already loaded before this listener was
  // registered — otherwise the shopifyCustomerPrivacyApiLoaded event has
  // already fired and would never be seen. Only a stored choice is applied.
  // JORS-3855: with no stored choice nothing is sent to Shopify, because the
  // Customer Privacy API records consent only on a visitor interaction; the
  // region default and the Consent Mode default (denied, theme.liquid) hold.
  const applyStoredConsent = () => {
    const consentStatus = localStorage.getItem('termly-consent-status');
    if (consentStatus === 'accepted') {
      enableTracking();
    } else if (consentStatus === 'custom') {
      let stored = null;
      try { stored = JSON.parse(localStorage.getItem('termly-consent-categories')); } catch (e) { stored = null; }
      applyCategories(stored || ALL_OFF);
    } else if (consentStatus === 'declined') {
      disableTracking();
    }
  };

  if (window.Shopify && window.Shopify.customerPrivacy) {
    applyStoredConsent();
  } else {
    window.addEventListener('shopifyCustomerPrivacyApiLoaded', applyStoredConsent);
  }

  // MutationObserver to bind the buttons
  const observer = new MutationObserver((mutations, obs) => {
    const acceptBtn = document.querySelector('[data-tid="banner-accept"]');
    const declineBtn = document.querySelector('[data-tid="banner-decline"]');

    if (acceptBtn && declineBtn) {
      acceptBtn.addEventListener('click', () => {
        localStorage.setItem('termly-consent-status', 'accepted');
        enableTracking();
      }, true);

      declineBtn.addEventListener('click', () => {
        localStorage.setItem('termly-consent-status', 'declined');
        disableTracking();
      }, true);

      obs.disconnect();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // JORS-3856 AC-04: the banner bindings above never see a choice saved in
  // the preference centre. Termly's Event API reports every consent change,
  // whichever control made it, so the centre's save reaches the Shopify
  // Customer Privacy API through the same path as the banner.
  // JORS-3856 D3: Termly's resource-blocker fires a `consent` event on page
  // load, before any interaction, carrying its regional default (all
  // non-essential categories off) — verified live on preview 199617904988,
  // 24/09/2026: under 1s after a fresh load, with no stored choice, this
  // event alone wrote `declined` via onTermlyConsent below. Gate it behind
  // an actual click on the centre's own commit controls (selectors
  // verified live the same day): "Save and Finish" (.t-saveButton, appears
  // only after a toggle), "Decline All" (.t-declineAllButton) or "Allow
  // All" (.t-allowAllButton). Delegated on document since these controls
  // only exist once Termly renders the centre.
  let centreInteracted = false;
  document.addEventListener('click', (e) => {
    if (e.target.closest('.t-saveButton, .t-declineAllButton, .t-allowAllButton')) {
      centreInteracted = true;
    }
  }, true);

  const onTermlyConsent = (data) => {
    if (!centreInteracted) return;
    const state = (data && data.consentState) ||
      (window.Termly && typeof window.Termly.getConsentState === 'function' ? window.Termly.getConsentState() : null);
    if (!state) return;
    applyCategories(storeCategories(state));
  };

  // JORS-4902 (variant, not released): the resource-blocker is served with async, so on a slow
  // connection Termly can arrive after the former 5 s window (20 tries). 120 tries at 250 ms = 30 s.
  let termlyTries = 0;
  const bindTermlyConsent = () => {
    if (window.Termly && typeof window.Termly.on === 'function') {
      window.Termly.on('consent', onTermlyConsent);
    } else if (termlyTries < 120) {
      termlyTries++;
      setTimeout(bindTermlyConsent, 250);
    }
  };
  bindTermlyConsent();
});
